/**
 * Logger Utility
 * Centralized logging utility with environment-based control
 * Only logs in development environment to avoid console pollution in production
 */

import { isDev } from '@common'

/**
 * Log levels
 */
export const LOG_LEVELS = {
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
}

/**
 * Logger class for centralized logging
 */
class Logger {
    /**
     * Log debug message (only in development)
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments to log
     */
    debug(message, ...args) {
        if (isDev) {
            console.log(`[DEBUG] ${message}`, ...args)
        }
    }

    /**
     * Log info message (only in development)
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments to log
     */
    info(message, ...args) {
        if (isDev) {
            console.info(`[INFO] ${message}`, ...args)
        }
    }

    /**
     * Log warning message (always logged)
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments to log
     */
    warn(message, ...args) {
        if (isDev) {
            console.warn(`[WARN] ${message}`, ...args)
        }
    }

    /**
     * Log error message (always logged)
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments to log
     */
    error(message, ...args) {
        // Errors are always logged, even in production
        console.error(`[ERROR] ${message}`, ...args)
    }

    /**
     * Log with custom prefix (only in development)
     * Useful for module-specific logging
     * @param {string} prefix - Log prefix (e.g., module name)
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments to log
     */
    logWithPrefix(prefix, message, ...args) {
        if (isDev) {
            console.log(`[${prefix}] ${message}`, ...args)
        }
    }
}

// Create singleton instance
export const logger = new Logger()

// Export default logger instance
export default logger
